/**
 * Citation utilities
 *
 * Shared helpers for inline source citations and source previews.
 *
 * Primary responsibilities:
 * - Normalize citation arrays stored as JSON strings
 * - Derive stable source labels, hostnames, and summaries
 *
 * Dependencies:
 * - favicon URL helpers
 */

import { getDomainFromUrl } from '@/lib/favicon'

export type CitationSource = {
  url: string
  title?: string | null
  description?: string | null
  summary?: string | null
  content?: string | null
  hostname?: string | null
}

function normalizeCitationSource(
  value: unknown
): CitationSource | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const record = value as Record<string, unknown>
  const url = typeof record.url === 'string' ? record.url.trim() : ''

  if (!url) {
    return null
  }

  return {
    url,
    title: typeof record.title === 'string' ? record.title.trim() : null,
    description:
      typeof record.description === 'string' ? record.description.trim() : null,
    summary: typeof record.summary === 'string' ? record.summary.trim() : null,
    content: typeof record.content === 'string' ? record.content.trim() : null,
    hostname:
      typeof record.hostname === 'string' ? record.hostname.trim() : null
  }
}

// --------------

export function parseCitationSources(
  value: string | CitationSource[] | null | undefined
): CitationSource[] {
  if (!value) {
    return []
  }

  if (Array.isArray(value)) {
    return value
      .map(normalizeCitationSource)
      .filter((source): source is CitationSource => source !== null)
  }

  try {
    const parsed = JSON.parse(value) as unknown
    if (!Array.isArray(parsed)) {
      return []
    }

    return parsed
      .map(normalizeCitationSource)
      .filter((source): source is CitationSource => source !== null)
  } catch {
    return []
  }
}

// --------------

function parseTrailingSourceLine(
  value: string
): CitationSource | null {
  const match = value.match(/^\[(\d+)\]\s+(.+?)\s+-\s+(https?:\/\/\S+)\s*$/)

  if (match) {
    const title = match[2]?.trim()
    const url = match[3]?.trim()

    if (!url) return null

    return {
      url,
      title: title && title !== url ? title : null
    }
  }

  const urlOnlyMatch = value.match(/^\[(\d+)\]\s+(https?:\/\/\S+)\s*$/)
  if (!urlOnlyMatch) return null

  return {
    url: urlOnlyMatch[2].trim(),
    title: null
  }
}

// --------------

export function extractTrailingCitationSources(content: string | null | undefined): {
  content: string
  sources: CitationSource[]
} {
  const sourceContent = typeof content === 'string' ? content : ''
  const normalized = sourceContent.trimEnd()
  if (!normalized) {
    return {
      content: sourceContent,
      sources: []
    }
  }

  const marker = '\nSources:\n'
  const markerIndex = normalized.lastIndexOf(marker)
  const startsWithMarker = normalized.startsWith('Sources:\n')

  const blockStart =
    markerIndex >= 0 ? markerIndex + 1 : startsWithMarker ? 0 : -1

  if (blockStart < 0) {
    return {
      content: sourceContent,
      sources: []
    }
  }

  const block = normalized.slice(blockStart)
  const lines = block.split('\n')

  if (lines[0]?.trim() !== 'Sources:') {
    return {
      content: sourceContent,
      sources: []
    }
  }

  const sources = lines
    .slice(1)
    .map((line) => line.trim())
    .filter(Boolean)
    .map(parseTrailingSourceLine)
    .filter((source): source is CitationSource => source !== null)

  if (sources.length === 0 || sources.length !== lines.slice(1).filter(Boolean).length) {
    return {
      content: sourceContent,
      sources: []
    }
  }

  return {
    content: normalized.slice(0, blockStart).trimEnd(),
    sources
  }
}

// --------------

export function mergeCitationSources(
  primary: CitationSource[],
  secondary: CitationSource[]
): CitationSource[] {
  const merged: CitationSource[] = []
  const seen = new Set<string>()

  for (const source of [...primary, ...secondary]) {
    const key = source.url.trim()
    if (!key || seen.has(key)) continue
    seen.add(key)
    merged.push(source)
  }

  return merged
}

// --------------

export function getCitationSourceHostname(
  source: CitationSource
): string {
  const explicitHostname =
    typeof source.hostname === 'string' ? source.hostname.trim() : ''

  if (explicitHostname) {
    return explicitHostname.replace(/^www\./, '')
  }

  return getDomainFromUrl(source.url)?.replace(/^www\./, '') || 'source'
}

// --------------

export function getCitationSourceTitle(
  source: CitationSource
): string {
  const title = typeof source.title === 'string' ? source.title.trim() : ''

  if (title) {
    return title
  }

  return getCitationSourceHostname(source)
}

// --------------

export function getCitationSourceSummary(
  source: CitationSource
): string | null {
  const summaryFields = [
    source.description,
    source.summary,
    source.content
  ]

  for (const value of summaryFields) {
    if (typeof value !== 'string') {
      continue
    }

    const trimmed = value.trim()
    if (trimmed) {
      return trimmed
    }
  }

  return null
}
